// FormikBgpSwitch.jsx
import React from "react";
import { useField } from "formik";
import { Switch } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";

/**
 * A specialized switch for BGP that manages an object:
 * { enabled: boolean, id: string }
 */
//Rendered from parent:
// <FormikBgpSwitch
// 	label={translations["ToggleBGPSession"]}
// 	name={`configuration.${addon.type}`}
// 	bgpOptions={addon.options}
// 	handleAddonChange={handleAddonChange}
// />

const FormikBgpSwitch = ({ label, name, required, bgpOptions = [], handleAddonChange }) => {
	const { translations } = usePageSettings();
	// The field name might be "configuration.bgp"
	// We'll store an object: { enabled: true/false, id: "..." }
	const [field, , helpers] = useField(name);
	const { setValue } = helpers;

	// The current value for this entire field
	const valueObj = field.value || { enabled: false, id: "" };

	// Decide if the switch is "on" based on valueObj.enabled
	const isOn = valueObj.enabled === true;

	// If you want to store a specific "id" when on, you might pass the array of BGP options:
	// e.g. bgpOptions = [{ id: "abc123" }, ...]
	const firstBGPOptionId = bgpOptions[0]?.id || "";

	const handleChange = (nextChecked) => {
		if (nextChecked) {
			setValue({
				enabled: true,
				id: firstBGPOptionId,
			});
		} else {
			setValue({
				enabled: false,
				id: "",
			});
		}
	};

	// Optional: define a fade-up variant:
	const fadeUp = {
		initial: { opacity: 0, y: 8 },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: 8 },
		transition: { duration: 0.2 },
	};

	return (
		<div className={`${isOn ? "opacity-100" : "opacity-50"} transition-opacity duration-400 flex flex-col gap-2`}>
			{/* Label + Switch */}
			<div className="flex h-fit items-center gap-2 mt-3">
				<label htmlFor={name} className="flex gap-1">
					{label}
					{required && <span className="text-ipxon-magenta">*</span>}
				</label>

				<Switch
					checked={isOn}
					onChange={handleChange}
					className={`
            relative inline-flex items-center
            h-4 w-[29px] cursor-pointer rounded-full p-[1px]
            transition-colors duration-200 ease-in-out
            ${isOn ? "bg-ipxon-magenta" : "bg-white border border-zinc-200"}
          `}
				>
					<span
						aria-hidden="true"
						className={`
              inline-block h-[13px] w-[13px] transform rounded-full bg-gray-400
              transition duration-200 ease-in-out
              ${isOn ? "translate-x-[14px] !bg-white" : "translate-x-0"}
            `}
					/>
				</Switch>
			</div>

			<AnimatePresence>
				{isOn && (
					<motion.div {...fadeUp} className="flex w-full text-sm text-ipxon-medium-dark-gray">
						{translations["ToggleBGPSessionDisclaimer"]}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default FormikBgpSwitch;
